import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRandomHotTakes } from "@/lib/hooks/use-random-hot-takes";
import { useActivityDefinition, useSendActivity } from "@/lib/api/activities";
import { usePostHogEvent } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { FireIcon } from "@heroicons/react/24/outline";
import {
  isCustomStatementId,
  makeCustomStatementId,
  type HotTakeAnswer,
  type HotTakeBankStatement,
  type HotTakesConfigStatement,
} from "@somethings-ui/core/hot-takes";
import { Shuffle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { HotTakesMode } from "./hot-takes-mode-picker";
import { StatementRow, type StatementRowValue } from "./statement-row";

const MAX_ROWS = 50;
const MIN_CHARS = 5;
const MAX_CHARS = 135;
const RANDOM_INITIAL_COUNT = 5;

interface HotTakesDefinition {
  staticContent: {
    statements: {
      bank: HotTakeBankStatement[];
    };
  };
}

interface HotTakesComposerProps {
  isOpen: boolean;
  mode: HotTakesMode;
  mentorshipId: string;
  teenName: string;
  onClose: () => void;
  onBack: () => void;
  onSent: () => void;
}

let localRowId = 0;
const makeDraftRowId = () => `draft:${++localRowId}`;

export function HotTakesComposer({
  isOpen,
  mode,
  mentorshipId,
  teenName: _teenName,
  onClose,
  onBack,
  onSent,
}: HotTakesComposerProps) {
  const { data: definition } =
    useActivityDefinition<HotTakesDefinition>("hot_takes");
  const pool = useMemo(
    () =>
      definition?.staticContent.statements.bank.filter(
        (statement) => statement.approved,
      ) ?? [],
    [definition],
  );
  const { getNext, shuffleOne } = useRandomHotTakes(pool);
  const sendMutation = useSendActivity();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const [rows, setRows] = useState<StatementRowValue[]>([]);

  useEffect(() => {
    if (!isOpen || mode !== "write") return;
    setRows([
      {
        id: makeDraftRowId(),
        text: "",
        mentorAnswer: null,
        fromBankUnmodified: false,
      },
    ]);
  }, [isOpen, mode]);

  useEffect(() => {
    if (!isOpen || mode !== "random") return;
    if (pool.length === 0) return;
    const picks = getNext(RANDOM_INITIAL_COUNT);
    setRows(
      picks.map((statement) => ({
        id: statement.id,
        text: statement.text,
        mentorAnswer: null,
        fromBankUnmodified: true,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, pool.length]);

  const completedCount = useMemo(
    () =>
      rows.filter((r) => r.mentorAnswer !== null && r.text.length > 0).length,
    [rows],
  );

  const canSend = useMemo(() => {
    if (rows.length < 1 || rows.length > MAX_ROWS) return false;
    return rows.every(
      (r) =>
        r.text.trim().length >= MIN_CHARS &&
        r.text.trim().length <= MAX_CHARS &&
        r.mentorAnswer !== null,
    );
  }, [rows]);

  const updateRow = useCallback(
    (index: number, patch: Partial<StatementRowValue>) => {
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const handleTextChange = useCallback(
    (index: number, text: string) => {
      const row = rows[index];
      if (!row) return;
      const prevText = row.text;
      const isEditing = row.fromBankUnmodified && text !== prevText;
      updateRow(index, {
        text,
        fromBankUnmodified: isEditing ? false : row.fromBankUnmodified,
      });
      dispatchEvent("activity.mentor.statement_edited", {
        mentorshipId,
        statementId: row.id,
      });
    },
    [rows, updateRow, dispatchEvent, mentorshipId],
  );

  const handleAnswerChange = useCallback(
    (index: number, answer: HotTakeAnswer) => {
      updateRow(index, { mentorAnswer: answer });
      dispatchEvent("activity.mentor.answer_toggled", {
        mentorshipId,
        answer,
        mode,
      });
    },
    [updateRow, dispatchEvent, mentorshipId, mode],
  );

  const handleShuffle = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row) return;
      const next = shuffleOne(row.id);
      if (!next) return;
      setRows((prev) =>
        prev.map((r, i) =>
          i === index
            ? {
                id: next.id,
                text: next.text,
                mentorAnswer: null,
                fromBankUnmodified: true,
              }
            : r,
        ),
      );
      dispatchEvent("activity.mentor.statement_shuffled", {
        mentorshipId,
        fromStatementId: row.id,
        toStatementId: next.id,
      });
    },
    [rows, shuffleOne, dispatchEvent, mentorshipId],
  );

  const handleShuffleAll = useCallback(() => {
    // Replace every unedited bank row with a fresh random draw.
    // Custom / edited rows are left alone so mentor work isn't lost.
    // shuffleOne mutates seenIds, so resolve all draws before setRows to keep
    // the updater pure (StrictMode double-invokes impure updaters in dev).
    const nextRows = rows.map((r) => {
      if (!r.fromBankUnmodified) return r;
      const next = shuffleOne(r.id);
      if (!next) return r;
      return {
        id: next.id,
        text: next.text,
        mentorAnswer: null,
        fromBankUnmodified: true,
      };
    });
    setRows(nextRows);
    dispatchEvent("activity.mentor.shuffle_all", { mentorshipId });
  }, [rows, shuffleOne, dispatchEvent, mentorshipId]);

  const handleDelete = useCallback(
    (index: number) => {
      if (rows.length <= 1) return;
      const row = rows[index];
      setRows((prev) => prev.filter((_, i) => i !== index));
      if (row) {
        dispatchEvent("activity.mentor.statement_deleted", {
          mentorshipId,
          statementId: row.id,
        });
      }
    },
    [rows, dispatchEvent, mentorshipId],
  );

  const handleAddTake = useCallback(() => {
    if (rows.length >= MAX_ROWS) return;
    if (mode === "write") {
      const draftId = makeDraftRowId();
      setRows((prev) => [
        ...prev,
        {
          id: draftId,
          text: "",
          mentorAnswer: null,
          fromBankUnmodified: false,
        },
      ]);
      dispatchEvent("activity.mentor.statement_added", {
        mentorshipId,
        statementId: draftId,
      });
      return;
    }

    const next = getNext(1)[0];
    if (!next) return;
    setRows((prev) => [
      ...prev,
      {
        id: next.id,
        text: next.text,
        mentorAnswer: null,
        fromBankUnmodified: true,
      },
    ]);
    dispatchEvent("activity.mentor.statement_added", {
      mentorshipId,
      statementId: next.id,
    });
  }, [rows.length, mode, getNext, dispatchEvent, mentorshipId]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    const statements: HotTakesConfigStatement[] = rows.map((r) => ({
      id:
        r.fromBankUnmodified && !isCustomStatementId(r.id)
          ? r.id
          : makeCustomStatementId(),
      text: r.text.trim(),
      mentorAnswer: r.mentorAnswer as HotTakeAnswer,
    }));

    sendMutation.mutate(
      {
        mentorshipId,
        activityType: "hot_takes",
        config: { statements },
      },
      {
        onSuccess: () => {
          onSent();
          onClose();
        },
      },
    );
  }, [canSend, rows, sendMutation, mentorshipId, onSent, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-[760px] max-w-[760px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[760px]">
        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-[#e8e8e6] px-8 pb-6 pt-8">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <FireIcon
                  aria-hidden
                  strokeWidth={2}
                  className="h-6 w-6 shrink-0 text-zinc-800"
                />
                <DialogTitle className="text-2xl font-bold tracking-tight">
                  Hot Takes
                </DialogTitle>
              </div>
              <p className="text-sm text-secondary">
                Share your honest opinion on these statements
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-secondary">
              {completedCount} of {rows.length} completed
            </p>
            {mode === "random" && (
              <button
                type="button"
                onClick={handleShuffleAll}
                className="inline-flex items-center gap-1.5 text-sm text-secondary hover:underline"
              >
                <Shuffle className="h-3 w-3" />
                Shuffle
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div className="flex flex-col gap-6">
            {rows.map((row, i) => (
              <StatementRow
                key={`${row.id}-${i}`}
                index={i}
                value={row}
                canDelete={rows.length > 1}
                onTextChange={(text) => handleTextChange(i, text)}
                onAnswerChange={(a) => handleAnswerChange(i, a)}
                onShuffle={() => handleShuffle(i)}
                onDelete={() => handleDelete(i)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleAddTake}
            disabled={rows.length >= MAX_ROWS}
            className={cn(
              "mt-6 flex w-full items-center justify-center rounded-xl border border-dashed border-[#c7c7c7] bg-white px-4 py-3 text-sm text-secondary",
              "hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            + add take
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 border-t border-[#f2f0ec] px-8 pb-6 pt-6">
          <button
            type="button"
            onClick={onBack}
            className={cn(
              "flex-1 rounded-xl border border-[#c7c7c7] bg-white py-3 text-base font-bold text-secondary",
              "hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-indigo-500",
            )}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || sendMutation.isPending}
            className={cn(
              "flex-1 rounded-xl py-3 text-base font-bold text-white",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500",
              canSend
                ? "bg-[#1f1e1d] hover:bg-black"
                : "cursor-not-allowed bg-[#c7c7c7]",
            )}
          >
            {sendMutation.isPending ? "Sending…" : "Send"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
